import * as React from 'react'
import { Files } from './with-file-state'
import { buildFileInput } from './build-file-input'
import { buildParagraph } from './build-paragraph'
import { buildAlert } from './build-alert'
import { buildTextfield } from './build-text-field'
import { buildSelect } from './build-select'
import { buildRadio } from './build-radio'
import { buildTextarea } from './build-text-area'
import { SearchField } from '../../components/answers/searchfield'
import { PopularQuestions } from '../../components/answers/popularquestions'
import { Category } from '../../components/answers/category'
import { ArticleLink } from '../../components/answers/articleLink'
import { FormButton } from '../../components/forms/formbutton'
import { buildThumbs } from './build-thumbs'
import { buildStarRating } from './build-star-rating'
import { buildSubmitButton } from './build-submit-button'
import { buildConsent } from './build-consent'
import { FormValues, FileTextValue } from '../../reducers/form-values'
import { FormMetadata, Form } from './types'
import { buildCheckbox } from './build-checkbox'
import { buildStar1to5Rating } from './build-star1to5-rating'
import { FormEntryWithState } from './formbuilder'

export type buttonClickHandlerFn = (type: string) => void
export type inputValueChangeFn = (
  id: string,
  type: string,
  value: string
) => void
export type blurHandlerFn = (id: string) => void
export type addFilesFn = (
  id: string,
  files: Array<File>,
  limit: number
) => void
export type removeFileFn = (id: string, position: number) => void

export function build(
  userId: number,
  formId: number,
  data: FormEntryWithState[],
  values: FormValues,
  files: Files,
  inputValueChange: inputValueChangeFn,
  blurHandler: blurHandlerFn,
  buttonClickHandler: buttonClickHandlerFn,
  addFiles: addFilesFn,
  removeFile: removeFileFn,
  metadata: FormMetadata
): React.ReactNode {
  return data.reduce<JSX.Element[]>((acc, cur, index) => {
    if (!cur.show) {
      return acc
    } else {
      switch (cur.field_type) {
        case 'paragraph':
          return [...acc, buildParagraph(cur, metadata)]
        case 'alert':
          return [...acc, buildAlert(index, cur, metadata)]
        case 'textfield':
          return [
            ...acc,
            buildTextfield(
              cur,
              values[cur.qid] as FileTextValue,
              inputValueChange,
              blurHandler
            )
          ]
        case 'select':
          return [
            ...acc,
            buildSelect(cur, values[cur.qid] as FileTextValue, inputValueChange)
          ]
        case 'radio':
          return [...acc, buildRadio(cur, values[cur.qid] as FileTextValue, inputValueChange)]
        case 'checkbox':
          return [
            ...acc,
            buildCheckbox(cur, values[cur.qid] as FileTextValue, inputValueChange)
          ]
        case 'textarea':
          return [
            ...acc,
            buildTextarea(
              cur,
              values[cur.qid] as FileTextValue,
              inputValueChange,
              blurHandler
            )
          ]
        case 'thumbs':
          return [
            ...acc,
            buildThumbs(cur, values[cur.qid] as FileTextValue, inputValueChange)
          ]
        case 'stars':
          return [
            ...acc,
            buildStarRating(cur, values[cur.qid] as FileTextValue, inputValueChange)
          ]
        case 'stars1to5':
          return [
            ...acc,
            buildStar1to5Rating(cur, values[cur.qid] as FileTextValue, inputValueChange)
          ]
        case 'searchField':
          return [
            ...acc,
            <div className='col-xs-12' key={index}>
              <SearchField
                userId={userId}
                answersId={formId}
                shouldShowResults
                shouldShowSuggestions
              />
            </div>
          ]
        case 'popularQuestions':
          return [
            ...acc,
            <PopularQuestions
              key={index}
              numberOfItems={cur.numbItems}
              categoryIds={cur.cats}
            />
          ]
        case 'categoryQuestions':
          return [...acc, <Category key={index} field={cur} />]
        case 'articleLink':
          return [...acc, <ArticleLink key={index} field={cur} />]
        case 'submitButton':
          return [...acc, buildSubmitButton(cur, buttonClickHandler)]
        case 'consent':
          return [
            ...acc,
            buildConsent(cur, values[cur.qid] as FileTextValue, inputValueChange)
          ]
        case 'formButton':
          return [
            ...acc,
            <FormButton
              key={index}
              userId={userId}
              label={cur.label}
              connectedFormId={Number(cur.connectedForm)}
              isSticky={cur.sticky === '1'}
            />
          ]
        case 'fileInput':
          return [
            ...acc,
            buildFileInput(cur, files[cur.qid], addFiles, removeFile)
          ]
        default:
          return acc
      }
    }
  }, [])
}
